import { DotLottie } from 'https://cdn.jsdelivr.net/npm/@lottiefiles/dotlottie-web/+esm';

if (!customElements.get('proof-cards')) {
  class ProofCards extends HTMLElement {
    connectedCallback() {
      this._waitForGsap();
    }

    _waitForGsap() {
      if (typeof gsap !== 'undefined' && typeof Observer !== 'undefined') {
        this._init();
      } else {
        requestAnimationFrame(() => this._waitForGsap());
      }
    }

    _init() {
      gsap.registerPlugin(Observer);

      this.cards = [...this.querySelectorAll('[data-proof-card]')];
      this.slides = [...this.querySelectorAll('[data-carousel-slide]')];
      this.captions = [...this.querySelectorAll('[data-carousel-caption]')];
      this.dots = [...this.querySelectorAll('[data-carousel-dot]')];
      this.prevBtn = this.querySelector('[data-carousel-prev]');
      this.nextBtn = this.querySelector('[data-carousel-next]');
      this.slideCount = this.slides.length;
      this.current = 0;
      this.animating = false;

      this._lottieBySlide = {};
      this._noiseActive = false;
      this._noiseRaf = null;
      this._sectionVisible = false;

      gsap.set(this.slides.slice(1), { autoAlpha: 0 });
      gsap.set(this.captions.slice(1), { autoAlpha: 0, position: 'absolute' });

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this._goTo(this.current - 1));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this._goTo(this.current + 1));
      }
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this._goTo(parseInt(dot.dataset.carouselDot)));
      });

      const track = this.querySelector('[data-carousel-track]');
      if (track) {
        let startX = 0, startY = 0, axis = null;

        track.addEventListener('touchstart', (e) => {
          startX = e.changedTouches[0].clientX;
          startY = e.changedTouches[0].clientY;
          axis = null;
        }, { passive: true });

        track.addEventListener('touchmove', (e) => {
          if (axis) return;
          const dx = Math.abs(e.changedTouches[0].clientX - startX);
          const dy = Math.abs(e.changedTouches[0].clientY - startY);
          if (dx + dy > 30) axis = dx > dy * 2 ? 'x' : 'y';
        }, { passive: true });

        track.addEventListener('touchend', (e) => {
          if (axis !== 'x') return;
          const dx = e.changedTouches[0].clientX - startX;
          if (Math.abs(dx) > 50) {
            this._goTo(this.current + (dx < 0 ? 1 : -1));
          }
        }, { passive: true });
      }

      this._initLottieStacks();
      this._observeEntry();
      this._observeLazyVideo();
    }

    _initLottieStacks() {
      const stacks = this.querySelectorAll('[data-lottie-stack]');
      if (!stacks.length) return;

      const maxDpr = Math.min(window.devicePixelRatio || 1, 1.5);

      stacks.forEach(stack => {
        const slide = stack.closest('[data-carousel-slide]');
        if (!slide) return;
        const slideIndex = parseInt(slide.dataset.carouselSlide);

        const canvases = stack.querySelectorAll('canvas[data-lottie-src]');
        const noiseOverlay = stack.querySelector('[data-noise-overlay]');

        const sizeCanvases = () => {
          const w = stack.clientWidth;
          const h = stack.clientHeight;
          canvases.forEach(c => {
            const dpr = maxDpr;
            c.width = Math.round(w * dpr);
            c.height = Math.round(h * dpr);
          });
          if (noiseOverlay) {
            noiseOverlay.width = Math.round(w * maxDpr);
            noiseOverlay.height = Math.round(h * maxDpr);
          }
        };

        sizeCanvases();

        const entry = {
          stack, slideIndex, instances: [], canvases, noiseOverlay,
          noiseSource: null, noiseCtx: null, filledCanvas: null,
          dpr: maxDpr, active: false, loaded: false, started: false
        };
        this._lottieBySlide[slideIndex] = entry;

        let resizeId;
        window.addEventListener('resize', () => {
          clearTimeout(resizeId);
          resizeId = setTimeout(() => {
            sizeCanvases();
            if (!entry.loaded) return;
            const w = stack.clientWidth;
            const h = stack.clientHeight;
            entry.noiseSource = this._generateNoise(
              Math.round(w * maxDpr),
              Math.round(h * maxDpr)
            );
            entry.instances.forEach(inst => inst.resize());
          }, 200);
        });
      });

      const carousel = this.querySelector('[data-results-carousel]');
      if (!carousel) return;

      const preloadObserver = new IntersectionObserver(([e]) => {
        if (!e.isIntersecting) return;
        preloadObserver.disconnect();
        for (const entry of Object.values(this._lottieBySlide)) {
          this._loadLotties(entry, entry.canvases, entry.dpr);
        }
      }, { rootMargin: '100% 0px 100% 0px' });
      preloadObserver.observe(carousel);

      const visObserver = new IntersectionObserver(([e]) => {
        this._sectionVisible = e.isIntersecting;
        this._syncCurrentSlotLottie();
      }, { threshold: 0.05 });
      visObserver.observe(carousel);
    }

    _loadLotties(entry, canvases, maxDpr) {
      let i = 0;
      const loadNext = () => {
        if (i >= canvases.length) {
          const w = entry.stack.clientWidth;
          const h = entry.stack.clientHeight;
          entry.noiseSource = this._generateNoise(
            Math.round(w * maxDpr),
            Math.round(h * maxDpr)
          );
          entry.loaded = true;

          if (entry.noiseOverlay) {
            entry.filledCanvas = entry.stack.querySelector('[data-lottie-layer="filled"]');
            entry.noiseCtx = entry.noiseOverlay.getContext('2d');
          }

          const filledInst = entry.instances[0];
          if (filledInst) {
            entry._filledInst = filledInst;
            entry._captionAfter = false;
          }

          if (this._sectionVisible && entry.slideIndex === this.current) {
            entry.active = true;
            this._playStack(entry);
          }
          return;
        }
        const canvas = canvases[i++];
        const src = canvas.dataset.lottieSrc;
        if (!src) { loadNext(); return; }
        const instance = new DotLottie({ canvas, src, autoplay: false, loop: true });
        entry.instances.push(instance);
        instance.addEventListener('load', () => loadNext());
      };
      loadNext();
    }

    _syncCurrentSlotLottie(restart) {
      for (const [idx, entry] of Object.entries(this._lottieBySlide)) {
        if (!entry.loaded) continue;
        const shouldPlay = this._sectionVisible && parseInt(idx) === this.current;
        if (shouldPlay && !entry.active) {
          entry.active = true;
          this._playStack(entry, restart);
        } else if (!shouldPlay && entry.active) {
          entry.active = false;
          this._freezeStack(entry);
        }
      }
    }

    _playStack(entry, restart) {
      if (restart || !entry.started) {
        entry.instances.forEach(inst => {
          if (entry.started) inst.stop();
          inst.play();
        });
        entry.started = true;
        entry._captionAfter = false;
        this._updateCaptionOpacity(entry.slideIndex, false);
      } else {
        entry.instances.forEach(inst => inst.unfreeze());
      }
      this._startCaptionPoll(entry);
      this._startNoiseLoop();
    }

    _freezeStack(entry) {
      entry.instances.forEach(inst => inst.freeze());
      if (entry._captionInterval) {
        clearInterval(entry._captionInterval);
        entry._captionInterval = null;
      }
      this._stopNoiseIfIdle();
    }

    _startCaptionPoll(entry) {
      if (entry._captionInterval) clearInterval(entry._captionInterval);
      if (!entry._filledInst) return;
      const inst = entry._filledInst;
      const caption = this.captions[entry.slideIndex];
      const invert = caption && caption.hasAttribute('data-invert-caption');
      entry._captionInterval = setInterval(() => {
        const f = inst.currentFrame;
        const total = inst.totalFrames;
        if (!total) return;
        let isAfter = f >= total * 0.27 && f < total * 0.73;
        if (invert) isAfter = !isAfter;
        if (isAfter !== entry._captionAfter) {
          entry._captionAfter = isAfter;
          this._updateCaptionOpacity(entry.slideIndex, isAfter);
        }
      }, 50);
    }

    _updateCaptionOpacity(slideIndex, isAfter) {
      const caption = this.captions[slideIndex];
      if (!caption) return;
      const before = caption.querySelector('[data-caption-before]');
      const after = caption.querySelector('[data-caption-after]');
      if (!before || !after) return;
      const dur = 500;
      before.style.transition = `opacity ${dur}ms ease`;
      after.style.transition = `opacity ${dur}ms ease`;
      before.style.opacity = isAfter ? '0.2' : '1';
      after.style.opacity = isAfter ? '1' : '0.2';
    }

    _startNoiseLoop() {
      if (this._noiseRaf) return;
      this._noiseActive = true;
      let frame = 0;

      const draw = () => {
        if (!this._noiseActive) { this._noiseRaf = null; return; }
        this._noiseRaf = requestAnimationFrame(draw);

        if (++frame % 4 !== 0) return;

        for (const entry of Object.values(this._lottieBySlide)) {
          if (!entry.active || !entry.noiseCtx || !entry.filledCanvas) continue;
          const ctx = entry.noiseCtx;
          const w = entry.noiseOverlay.width;
          const h = entry.noiseOverlay.height;
          if (!w || !h) continue;

          ctx.clearRect(0, 0, w, h);
          ctx.globalCompositeOperation = 'source-over';
          ctx.globalAlpha = 1;
          ctx.filter = `blur(${5 * entry.dpr}px)`;
          ctx.drawImage(entry.filledCanvas, 0, 0, w, h);
          ctx.filter = 'none';
          ctx.globalCompositeOperation = 'source-in';
          if (entry.noiseSource) ctx.drawImage(entry.noiseSource, 0, 0, w, h);
          ctx.globalCompositeOperation = 'source-over';
        }
      };

      this._noiseRaf = requestAnimationFrame(draw);
    }

    _stopNoiseIfIdle() {
      const anyActive = Object.values(this._lottieBySlide).some(e => e.active && e.loaded);
      if (anyActive) return;
      this._noiseActive = false;
      if (this._noiseRaf) {
        cancelAnimationFrame(this._noiseRaf);
        this._noiseRaf = null;
      }
    }

    _generateNoise(w, h) {
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      const imageData = ctx.createImageData(w, h);
      const d = imageData.data;
      for (let i = 0; i < d.length; i += 4) {
        const v = (Math.random() * 255) | 0;
        d[i] = v; d[i + 1] = v; d[i + 2] = v; d[i + 3] = 255;
      }
      ctx.putImageData(imageData, 0, 0);
      return canvas;
    }

    _goTo(index) {
      if (this.animating) return;
      if (index < 0) index = this.slideCount - 1;
      if (index >= this.slideCount) index = 0;
      if (index === this.current) return;
      this.animating = true;

      const outSlide = this.slides[this.current];
      const outCaption = this.captions[this.current];
      const inSlide = this.slides[index];
      const inCaption = this.captions[index];

      const tl = gsap.timeline({
        onComplete: () => {
          this.animating = false;
        },
      });

      tl.to(outSlide, {
        autoAlpha: 0,
        duration: 0.5,
        ease: 'power2.inOut',
      }, 0);

      tl.to(outCaption, {
        autoAlpha: 0,
        duration: 0.35,
        ease: 'power2.in',
      }, 0);

      tl.fromTo(inSlide,
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: 0.5, ease: 'power2.inOut' },
        0.15
      );

      tl.fromTo(inCaption,
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: 0.35, ease: 'power2.out' },
        0.25
      );

      this.current = index;
      this._animateDots();
      this._syncCurrentSlotLottie(true);
    }

    _animateDots() {
      this.dots.forEach((dot, i) => {
        const isActive = i === this.current;
        gsap.to(dot, {
          width: isActive ? 20 : 4,
          backgroundColor: isActive ? '#FFF4CA' : 'rgba(255, 244, 202, 0.4)',
          duration: 0.35,
          ease: 'power2.out',
        });
      });
    }

    _observeLazyVideo() {
      const video = this.querySelector('[data-lazy-video]');
      if (!video) return;

      video.style.opacity = '0';

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            video.querySelectorAll('source[data-src]').forEach((source) => {
              source.src = source.dataset.src;
              source.removeAttribute('data-src');
            });
            video.load();
            video.addEventListener('canplay', () => {
              video.play();
              video.animate(
                [{ opacity: 0 }, { opacity: 1 }],
                { duration: 600, easing: 'ease', fill: 'forwards' }
              );
            }, { once: true });
            observer.disconnect();
          }
        });
      }, { rootMargin: '200% 0px', threshold: 0 });

      observer.observe(video);
    }

    _observeEntry() {
      if (window.innerWidth < 1280) return;

      const showCards = () => {
        if (this._entryDone) return;
        this._entryDone = true;
        this.cards.forEach((card, i) => {
          card.animate([
            { opacity: 0, transform: 'translateY(40px)' },
            { opacity: 1, transform: 'translateY(0px)' },
          ], {
            duration: 800,
            delay: i * 50,
            easing: 'linear',
            fill: 'forwards',
          });
        });
      };

      this.cards.forEach((card) => {
        card.style.opacity = '0';
        card.style.transform = 'translateY(40px)';
        card.style.transition = 'none';
      });

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            showCards();
            observer.disconnect();
          }
        });
      }, { threshold: 0.05 });

      observer.observe(this);

      setTimeout(showCards, 3000);
    }
  }

  customElements.define('proof-cards', ProofCards);
}
